/**
 * TalentLens AI - CV Detail Page
 * Redesigned with better UX for recruiters - optimized space utilization
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouter } from '@tanstack/react-router';
import api from '../lib/api';
import { useAuthStore, canDeleteCV } from '../store/auth';

type TabType = 'overview' | 'experience' | 'education' | 'original';

export default function CVDetailPage() {
  const params = useParams({ strict: false }) as { cvId: string };
  const cvId = params.cvId;
  const { user } = useAuthStore();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { data, isLoading, error } = useQuery({
    queryKey: ['cv', cvId],
    queryFn: () => api.getCV(cvId),
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryCV(cvId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cv', cvId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteCV(cvId),
    onSuccess: () => router.navigate({ to: '/cvs' }),
  });

  const handleDownload = async () => {
    try {
      const { data } = await api.getDownloadUrl(cvId);
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to load CV</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">The CV you're looking for doesn't exist or couldn't be loaded.</p>
        <Link to="/cvs" className="text-primary-600 hover:text-primary-700 font-medium">
          &larr; Back to CVs
        </Link>
      </div>
    );
  }

  const cv = data.data;
  const parsed = cv.parsedData;

  // Calculate stats
  const totalYearsExp = parsed?.experience?.reduce((acc: number, exp: any) => {
    const start = new Date(exp.start_date);
    const end = exp.end_date ? new Date(exp.end_date) : new Date();
    return acc + (end.getFullYear() - start.getFullYear());
  }, 0) || 0;

  const topSkills = parsed?.skills?.slice(0, 5) || [];
  const skillCategories = categorizeSkills(parsed?.skills || []);

  return (
    <div className="space-y-5">
      {/* Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Profile Content */}
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-5">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-purple-500 flex items-center justify-center text-xl font-bold text-white shadow-sm flex-shrink-0">
              {(parsed?.name || '?').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                <div className="space-y-2">
                  {/* Name & Status */}
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {parsed?.name || cv.filename}
                    </h1>
                    <StatusBadge status={cv.status} />
                  </div>

                  {/* Role/Title if available from latest experience */}
                  {parsed?.experience?.[0] && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {parsed.experience[0].title} at {parsed.experience[0].company}
                    </p>
                  )}

                  {/* Contact Details - Inline */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 dark:text-gray-400">
                    {parsed?.email && (
                      <a
                        href={`mailto:${parsed.email.toLowerCase()}`}
                        className="flex items-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      >
                        <MailIcon className="w-3.5 h-3.5" />
                        {parsed.email.toLowerCase()}
                      </a>
                    )}
                    {parsed?.phone && (
                      <span className="flex items-center gap-1.5">
                        <PhoneIcon className="w-3.5 h-3.5" />
                        {parsed.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      Added {new Date(cv.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Action Buttons - All grouped together */}
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  {/* Primary: Contact */}
                  {parsed?.email && (
                    <button
                      onClick={() => setShowEmailModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <MailIcon className="w-4 h-4" />
                      Contact
                    </button>
                  )}

                  {/* LinkedIn - Only if available */}
                  {parsed?.linkedin && (
                    <a
                      href={parsed.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-[#0A66C2] text-white text-sm font-medium rounded-lg hover:bg-[#004182] transition-colors"
                      title="View LinkedIn Profile"
                    >
                      <LinkedInIcon className="w-4 h-4" />
                    </a>
                  )}

                  {/* GitHub - Only if available */}
                  {parsed?.github && (
                    <a
                      href={parsed.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                      title="View GitHub Profile"
                    >
                      <GitHubIcon className="w-4 h-4" />
                    </a>
                  )}

                  {/* Download */}
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    title="Download CV"
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </button>

                  {/* More Actions Dropdown */}
                  <div className="relative group">
                    <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <MoreIcon className="w-4 h-4" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 hidden group-hover:block z-10">
                      <Link
                        to="/jobs"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <BriefcaseIcon className="w-4 h-4" />
                        Find Matching Jobs
                      </Link>
                      <Link
                        to="/search"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <SearchIcon className="w-4 h-4" />
                        Find Similar CVs
                      </Link>
                      {(cv.status === 'failed' || cv.status === 'partial') && (
                        <button
                          onClick={() => retryMutation.mutate()}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <RefreshIcon className="w-4 h-4" />
                          Retry Processing
                        </button>
                      )}
                      {canDeleteCV(user) && (
                        <button
                          onClick={() => {
                            if (confirm('Delete this CV?')) deleteMutation.mutate();
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete CV
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {parsed && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<BriefcaseIcon className="w-5 h-5" />}
            label="Experience"
            value={`${totalYearsExp}+ years`}
            color="blue"
          />
          <StatCard
            icon={<CodeIcon className="w-5 h-5" />}
            label="Skills"
            value={`${parsed.skills?.length || 0} skills`}
            color="green"
          />
          <StatCard
            icon={<GraduationIcon className="w-5 h-5" />}
            label="Education"
            value={getHighestEducation(parsed.education)}
            color="purple"
          />
          <StatCard
            icon={<AwardIcon className="w-5 h-5" />}
            label="Certifications"
            value={`${parsed.certifications?.length || 0} certs`}
            color="orange"
          />
        </div>
      )}

      {/* Parse Errors Alert */}
      {cv.parseErrors && cv.parseErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-300">Processing Issues</h3>
              <ul className="text-sm text-red-700 dark:text-red-400 mt-1 space-y-1">
                {cv.parseErrors.map((error: string, i: number) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {parsed && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Skills */}
          <div className="lg:col-span-1 space-y-6">
            {/* Top Skills */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <StarIcon className="w-5 h-5 text-yellow-500" />
                Top Skills
              </h2>
              <div className="space-y-3">
                {topSkills.map((skill: any, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">{skill.skill}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full"
                          style={{ width: `${Math.min((skill.years / 10) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400 w-12">{skill.years}y</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* All Skills by Category */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">All Skills</h2>
              {Object.entries(skillCategories).map(([category, skills]) => (
                skills.length > 0 && (
                  <div key={category} className="mb-4 last:mb-0">
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {category}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill: any, i: number) => (
                        <ClickableFilter
                          key={i}
                          type="skills"
                          value={skill.skill}
                          className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                          icon={
                            skill.years > 0 ? (
                              <span className="text-gray-400 dark:text-gray-500 ml-1">({skill.years}y)</span>
                            ) : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>

            {/* File Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">File Info</h2>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  getFileType(cv.mimeType) === 'PDF'
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <FileIcon className={`w-5 h-5 ${
                    getFileType(cv.mimeType) === 'PDF'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-white truncate" title={cv.filename}>
                    {cv.filename}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getFileType(cv.mimeType)} · {formatSize(cv.size)}
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <UserIcon className="w-4 h-4" />
                  <span>Uploaded by {cv.uploadedBy?.name || 'Unknown'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Experience & Education */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <div className="border-b dark:border-gray-700">
                <nav className="flex gap-1 p-2">
                  {([
                    { id: 'overview' as const, label: 'Overview', icon: null },
                    { id: 'experience' as const, label: 'Experience', icon: null },
                    { id: 'education' as const, label: 'Education', icon: null },
                    { id: 'original' as const, label: 'Original CV', icon: <DocumentIcon className="w-4 h-4" /> },
                  ]).map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                        activeTab === tab.id
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Recent Experience */}
                    {parsed.experience?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                          Recent Experience
                        </h3>
                        <div className="space-y-4">
                          {parsed.experience.slice(0, 2).map((exp: any, i: number) => (
                            <ExperienceCard key={i} experience={exp} />
                          ))}
                        </div>
                        {parsed.experience.length > 2 && (
                          <button
                            onClick={() => setActiveTab('experience')}
                            className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            View all {parsed.experience.length} positions →
                          </button>
                        )}
                      </div>
                    )}

                    {/* Education Summary */}
                    {parsed.education?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                          Education
                        </h3>
                        <div className="space-y-3">
                          {parsed.education.slice(0, 2).map((edu: any, i: number) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                <GraduationIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-white">{edu.degree}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{edu.institution}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-500">{formatDate(edu.graduation_date)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Certifications */}
                    {parsed.certifications?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                          Certifications
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {parsed.certifications.map((cert: any, i: number) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 rounded-lg"
                            >
                              <AwardIcon className="w-4 h-4" />
                              <span className="text-sm font-medium">{cert.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'experience' && (
                  <div className="space-y-4">
                    {parsed.experience?.length > 0 ? (
                      parsed.experience.map((exp: any, i: number) => (
                        <ExperienceCard key={i} experience={exp} detailed />
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-8">No experience data extracted</p>
                    )}
                  </div>
                )}

                {activeTab === 'education' && (
                  <div className="space-y-4">
                    {parsed.education?.length > 0 ? (
                      parsed.education.map((edu: any, i: number) => (
                        <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <GraduationIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">{edu.degree}</h4>
                            <p className="text-gray-600 dark:text-gray-400">{edu.institution}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                              Graduated {formatDate(edu.graduation_date)}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-8">No education data extracted</p>
                    )}

                    {/* Certifications in Education Tab */}
                    {parsed.certifications?.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                          Certifications
                        </h3>
                        {parsed.certifications.map((cert: any, i: number) => (
                          <div key={i} className="flex items-start gap-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl mb-3">
                            <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                              <AwardIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">{cert.name}</h4>
                              <p className="text-gray-600 dark:text-gray-400">{cert.issuer}</p>
                              {cert.issue_date && (
                                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                  Issued {formatDate(cert.issue_date)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'original' && (
                  <OriginalCVView cvId={cvId} filename={cv.filename} mimeType={cv.mimeType} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && parsed?.email && (
        <EmailComposerModal
          to={parsed.email}
          candidateName={parsed.name}
          onClose={() => setShowEmailModal(false)}
          onSuccess={() => setShowEmailModal(false)}
        />
      )}
    </div>
  );
}

// Experience Card Component with clickable company
function ExperienceCard({ experience, detailed = false }: { experience: any; detailed?: boolean }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <BriefcaseIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        {detailed && <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-2" />}
      </div>
      <div className="flex-1 pb-4">
        <ClickableFilter
          type="job_titles"
          value={experience.title}
          className="font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
        />
        <div className="flex items-center gap-1">
          <BuildingIcon className="w-3.5 h-3.5 text-primary-400 dark:text-primary-500" />
          <ClickableFilter
            type="companies"
            value={experience.company}
            className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
          />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          {formatDate(experience.start_date)} — {experience.end_date ? formatDate(experience.end_date) : 'Present'}
        </p>
        {detailed && experience.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{experience.description}</p>
        )}
        {detailed && experience.projects?.length > 0 && (
          <div className="mt-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Projects: </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">{experience.projects.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Clickable Filter Component - navigates to search with filter applied
function ClickableFilter({
  type,
  value,
  className = '',
  icon,
}: {
  type: 'skills' | 'companies' | 'job_titles' | 'certifications' | 'institutions';
  value: string;
  className?: string;
  icon?: React.ReactNode;
}) {
  const router = useRouter();

  const handleClick = () => {
    // Navigate to search with the filter applied
    const filterMap: Record<string, string> = {
      skills: 'skills',
      companies: 'companies',
      job_titles: 'jobTitles',
      certifications: 'certifications',
      institutions: 'institutions',
    };

    // Store the filter in sessionStorage so Search page can pick it up
    sessionStorage.setItem('searchFilter', JSON.stringify({
      type: filterMap[type],
      value: value,
    }));

    router.navigate({ to: '/search' });
  };

  return (
    <button
      onClick={handleClick}
      className={`text-left cursor-pointer transition-colors ${className}`}
      title={`Find all candidates with ${value}`}
    >
      {value}
      {icon}
    </button>
  );
}

// Original CV View Component
function OriginalCVView({ cvId, filename, mimeType }: { cvId: string; filename: string; mimeType: string }) {
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocument = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.getDownloadUrl(cvId);
      setViewUrl(data.downloadUrl);
    } catch (err) {
      setError('Failed to load document');
    } finally {
      setIsLoading(false);
    }
  };

  const isPDF = mimeType?.includes('pdf');
  const isWord = mimeType?.includes('word') || mimeType?.includes('openxmlformats');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isPDF ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            <FileIcon className={`w-5 h-5 ${
              isPDF ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
            }`} />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{filename}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isPDF ? 'PDF Document' : isWord ? 'Word Document' : 'Document'}
            </p>
          </div>
        </div>
        {!viewUrl && (
          <button
            onClick={loadDocument}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="w-4 h-4" />
                Loading...
              </>
            ) : (
              <>
                <EyeIcon className="w-4 h-4" />
                View Document
              </>
            )}
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      {viewUrl && (
        <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
          {isPDF ? (
            <iframe
              src={`${viewUrl}#toolbar=1&navpanes=0`}
              className="w-full h-[600px]"
              title="CV Document"
            />
          ) : (
            <div className="p-8 text-center bg-gray-50 dark:bg-gray-700/50">
              <FileIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Word documents cannot be previewed directly in the browser.
              </p>
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <DownloadIcon className="w-4 h-4" />
                Download to View
              </a>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-3">
                Or use{' '}
                <a
                  href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(viewUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Microsoft Office Online Viewer
                </a>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Loading Spinner Component
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  const colors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color as keyof typeof colors]}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    processed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    partial: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  };

  return (
    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

// Helper Functions
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

function formatSize(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getHighestEducation(education: any[] | undefined): string {
  if (!education || education.length === 0) return 'Not specified';

  // Define education level hierarchy
  const levelOrder: Record<string, number> = {
    'phd': 5,
    'doctorate': 5,
    'doctoral': 5,
    'master': 4,
    'mba': 4,
    'mca': 4,
    'msc': 4,
    'ms': 4,
    'ma': 4,
    'bachelor': 3,
    'bsc': 3,
    'bs': 3,
    'ba': 3,
    'bca': 3,
    'btech': 3,
    'be': 3,
    'associate': 2,
    'diploma': 1,
    'high school': 0,
  };

  let highest = { level: -1, label: 'Not specified' };

  for (const edu of education) {
    const degree = (edu.degree || '').toLowerCase();

    for (const [key, level] of Object.entries(levelOrder)) {
      if (degree.includes(key) && level > highest.level) {
        // Generate a clean label
        let label = '';
        if (level === 5) label = 'Doctorate';
        else if (level === 4) label = "Master's Degree";
        else if (level === 3) label = "Bachelor's Degree";
        else if (level === 2) label = "Associate's";
        else if (level === 1) label = 'Diploma';
        else label = 'High School';

        highest = { level, label };
      }
    }
  }

  return highest.label;
}

function getFileType(mimeType: string | undefined): string {
  if (!mimeType) return 'Document';

  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('openxmlformats-officedocument.wordprocessing')) return 'DOCX';
  if (mimeType.includes('msword')) return 'DOC';

  // Extract extension from complex MIME types
  const parts = mimeType.split('/');
  if (parts.length > 1) {
    const subtype = parts[1].toUpperCase();
    if (subtype.length <= 5) return subtype;
  }

  return 'Document';
}

function categorizeSkills(skills: any[]) {
  const categories: Record<string, any[]> = {
    'Programming': [],
    'Frameworks': [],
    'Databases': [],
    'Cloud & DevOps': [],
    'Other': [],
  };

  const programmingKeywords = ['javascript', 'python', 'java', 'c#', 'c++', 'typescript', 'go', 'rust', 'php', 'ruby', 'swift', 'kotlin'];
  const frameworkKeywords = ['react', 'angular', 'vue', 'node', 'express', 'django', 'flask', 'spring', '.net', 'laravel', 'rails'];
  const databaseKeywords = ['sql', 'mysql', 'postgresql', 'mongodb', 'redis', 'elasticsearch', 'oracle', 'cassandra'];
  const cloudKeywords = ['aws', 'azure', 'gcp', 'docker', 'kubernetes', 'ci/cd', 'jenkins', 'terraform', 'devops'];

  skills.forEach((skill) => {
    const name = skill.skill.toLowerCase();
    if (programmingKeywords.some(k => name.includes(k))) {
      categories['Programming'].push(skill);
    } else if (frameworkKeywords.some(k => name.includes(k))) {
      categories['Frameworks'].push(skill);
    } else if (databaseKeywords.some(k => name.includes(k))) {
      categories['Databases'].push(skill);
    } else if (cloudKeywords.some(k => name.includes(k))) {
      categories['Cloud & DevOps'].push(skill);
    } else {
      categories['Other'].push(skill);
    }
  });

  return categories;
}

// Email Composer Modal
function EmailComposerModal({
  to,
  candidateName,
  jobTitle,
  onClose,
  onSuccess,
}: {
  to: string;
  candidateName: string;
  jobTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [aiPurpose, setAiPurpose] = useState<'interview_invite' | 'follow_up' | 'rejection' | 'offer' | 'general'>('general');
  const [aiTone, setAiTone] = useState<'formal' | 'friendly' | 'professional'>('professional');
  const [aiContext, setAiContext] = useState('');

  const sendMutation = useMutation({
    mutationFn: () => api.sendEmail({
      to,
      subject,
      body,
      placeholders: { candidate_name: candidateName },
    }),
    onSuccess,
  });

  const aiComposeMutation = useMutation({
    mutationFn: () => api.aiComposeEmail({
      purpose: aiPurpose,
      candidateName,
      candidateEmail: to,
      jobTitle,
      additionalContext: aiContext || undefined,
      tone: aiTone,
    }),
    onSuccess: (data) => {
      if (data.data) {
        setSubject(data.data.subject);
        setBody(data.data.body);
        setShowAiPanel(false);
      }
    },
  });

  const purposeLabels = {
    interview_invite: 'Interview Invitation',
    follow_up: 'Follow Up',
    rejection: 'Rejection (Professional)',
    offer: 'Job Offer',
    general: 'General Outreach',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Candidate</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {showAiPanel && (
            <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">AI Email Composer</h4>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Purpose</label>
                  <select
                    value={aiPurpose}
                    onChange={(e) => setAiPurpose(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {Object.entries(purposeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tone</label>
                  <select
                    value={aiTone}
                    onChange={(e) => setAiTone(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="formal">Formal</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Additional Context (optional)</label>
                <input
                  type="text"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  placeholder="e.g., for Senior Developer role, interview next week..."
                  className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => aiComposeMutation.mutate()}
                  disabled={aiComposeMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {aiComposeMutation.isPending ? 'Generating...' : 'Generate Email'}
                </button>
                <button
                  onClick={() => setShowAiPanel(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Write manually
                </button>
              </div>

              {aiComposeMutation.isError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  Failed to generate email. Please try again.
                </p>
              )}
            </div>
          )}

          {!showAiPanel && (
            <button
              onClick={() => setShowAiPanel(true)}
              className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <SparklesIcon className="w-4 h-4" />
              Use AI to compose email
            </button>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
            <input
              type="email"
              value={to}
              disabled
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message..."
              rows={8}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => sendMutation.mutate()}
            disabled={!subject || !body || sendMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {sendMutation.isPending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Icons
function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

function GraduationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  );
}

function AwardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}
